import { useCallback, useEffect, useMemo, useReducer, useState } from "react";

import useEndorse from "@/utils/hooks/endorse/useEndorse";

export default function useSave({ mode = SaveMode.AUTO, ...options }) {
  const [saveState, setSaveState] = useState(SaveState.UNCHANGED);
  const [requestState, setRequestState] = useState(RequestState.NONE);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [touched, setTouched] = useState([]);
  const [internalSaveErrors, setInternalSaveErrors] = useState({});

  const [value, dispatch] = useReducer(options?.reducer, options?.initialValue);

  // Pass the options to the dispatch function and manage touched
  const dispatcher = useCallback((config) => {
    if (config.type === "set") {
      setSaveState(SaveState.UNCHANGED);
      setRequestState(RequestState.NONE);
      setShowAllErrors(false);
      setTouched([]);
      setInternalSaveErrors({});
    } else {
      setTouched((prev) => (prev.indexOf(config.type) === -1 ? [...prev, config.type] : prev));
    }

    dispatch(config);
  }, []);

  const { isValid, errors, allErrors } = useEndorse(
    value,
    options?.validator,
    touched,
    showAllErrors,
    internalSaveErrors,
  );

  const apiErrorHandler = useCallback((errors) => {
    const apiErrors = errors.reduce(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.field]: currentValue.message.reduce((_accumulator, _currentValue) => {
          if (_currentValue === "has already been taken" && currentValue.field === "exten") {
            return [..._accumulator, "Extension has already been taken."];
          }

          return [..._accumulator, _currentValue];
        }, []),
      }),
      [],
    );

    setInternalSaveErrors(apiErrors);
  }, []);

  const saveForm = useCallback(() => {
    if (isValid === undefined || isValid) {
      setShowAllErrors(false);

      if (options.onSubmit) {
        // When there is an onSubmit method, we start altering the state
        setSaveState(SaveState.SAVED);
        setRequestState(RequestState.PENDING);

        Promise.resolve(options.onSubmit())
          .then((response) => {
            // After success, execute the onSuccess function if there is one
            if (options?.onSuccess) {
              options.onSuccess(response);
            }

            apiErrorHandler([]);
            setRequestState(RequestState.SUCCESS);
          })
          .catch((error) => {
            apiErrorHandler(error?.data?.errors ?? []);
            setRequestState(RequestState.ERROR);
          });
      }
    } else {
      setSaveState(SaveState.AWAITING);
    }
  }, [apiErrorHandler, isValid, options]);

  // Function to trigger manual save
  const save = useCallback(() => {
    saveForm();
    setShowAllErrors(true);
  }, [saveForm]);

  // Manage requests
  useEffect(() => {
    const handler = setTimeout(() => {
      if (mode === SaveMode.AUTO) {
        if (saveState === SaveState.UNSAVED) {
          saveForm();
        } else if (saveState === SaveState.VALIDATE) {
          setShowAllErrors(true);
        }
      }
    }, options.delay ?? 1000);

    return () => clearTimeout(handler);
  }, [saveState, options.onSubmit, isValid, options.delay, saveForm, mode]);

  const formState = useMemo(() => {
    let status;

    if (
      saveState === SaveState.SAVED &&
      (requestState === RequestState.SUCCESS || requestState === RequestState.NONE)
    ) {
      status = FormState.SAVED;
    } else if (
      saveState === SaveState.UNCHANGED &&
      (requestState === RequestState.SUCCESS || requestState === RequestState.NONE)
    ) {
      status = FormState.UNCHANGED;
    } else if (saveState === SaveState.AWAITING) {
      status = FormState.AWAITING;
    } else if (saveState === SaveState.UNSAVED) {
      status = FormState.UNSAVED;
    } else if (requestState === RequestState.PENDING) {
      status = FormState.PENDING;
    } else if (requestState === RequestState.ERROR) {
      status = FormState.ERROR;
    } else {
      status = FormState.SAVED;
    }

    return { status, saveMode: mode };
  }, [mode, requestState, saveState]);

  return {
    setSaveState,
    formState,
    save,
    errors,
    allErrors,
    value,
    dispatch: dispatcher,
    isValid,
    pending: requestState === RequestState.PENDING,
  };
}

// Enums
const FormState = Object.freeze({
  UNCHANGED: "unchanged",
  UNSAVED: "unsaved",
  PENDING: "pending",
  SAVED: "saved",
  ERROR: "error",
  AWAITING: "awaiting",
});

const SaveState = Object.freeze({
  UNCHANGED: "unchanged",
  UNSAVED: "unsaved",
  SAVED: "saved",
  AWAITING: "awaiting",
});

const RequestState = Object.freeze({
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
  NONE: "none",
});

const SaveMode = Object.freeze({
  MANUAL: "manual",
  AUTO: "auto",
});

export { SaveState, RequestState, FormState, SaveMode };
